import { JsonForms } from '@jsonforms/react';
import Container from '@risk-smart/themed-cloudscape-components/container';
import Header from '@risk-smart/themed-cloudscape-components/header';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import {
  parseQuestionnaireResponseData,
  parseQuestionnaireSchemaForRendering,
  parseQuestionnaireUiSchemaForRendering,
} from '@risksmart-app/components/src/form-builder/questionnaireSchemaContracts';
import { rendererRegistry } from '@risksmart-app/components/src/form-builder/renderers/registry';
import {
  defaultSchema,
  defaultUISchema,
  useFormBuilderStore,
} from '@risksmart-app/components/src/form-builder/store/useFormBuilderStore';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import Loading from '@risksmart-app/components/src/loading';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { ThirdPartyResponseAction as Third_Party_Response_Enum_Action } from '@risksmart-app/domain/src/types/consts/third-party-response-action';
import { ThirdPartyResponseStatus as Third_Party_Response_Status_Enum } from '@risksmart-app/domain/src/types/consts/third-party-response-status';
import { getIsActionAllowed } from '@risksmart-app/shared/third-party/responses/responseUtils';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type {
  ObjectToolbarConfig,
  ToolbarActionConfig,
} from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ActionsButton from 'src/components/actions-button';
import type { ActionItem } from 'src/components/actions-button/ActionsButton';
import { useGetThirdPartyById } from 'src/hooks/queries/third-party/useGetThirdPartyById';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useShallow } from 'zustand/react/shallow';

import { useUpdateThirdPartyResponseStatus } from '@/hooks/mutations/third-party-response';
import { useGetThirdPartyResponseByIdSubscription } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { UpdateStatusSchemaFields } from '../../third-party/update/tabs/questionnaires/modals/schema';
import { RequestTypeOptions } from '../../third-party/update/tabs/questionnaires/modals/schema';
import { UpdateStatusModal } from '../../third-party/update/tabs/questionnaires/modals/UpdateStatusModal';
import { ResponseRequestHistory } from '../blocks/response-request-history';

const Page: FC = () => {
  const Id = useGetGuidParam('responseId');
  const navigate = useNavigate();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const { t: rt } = useTranslation(['common'], {
    keyPrefix: 'questionnaire_invite',
  });

  const [isDisabled, setIsDisabled] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [action, setAction] = useState<Third_Party_Response_Enum_Action>(
    Third_Party_Response_Enum_Action.RequestMoreInformation
  );
  const [actions, setActions] = useState<ActionItem[]>([]);

  const thirdPartyId = useGetGuidParam('id');
  const { getByValue } = useRating('third_party_response_status');
  const { data: parent } = useGetThirdPartyById({
    queryArgs: { thirdPartyId },
  });
  const { data, loading } = useGetThirdPartyResponseByIdSubscription({
    queryArgs: { id: Id },
  });
  const { setIsFormCustomisable } = useFormBuilderStore(
    useShallow((state) => ({
      setIsFormCustomisable: state.setIsFormCustomisable,
    }))
  );

  useEffect(() => {
    setIsFormCustomisable(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const status = data?.third_party_response_by_pk?.Status;
    const isDisabled =
      status !== Third_Party_Response_Status_Enum.AwaitingReview;

    setIsDisabled(isDisabled);

    setActions([
      {
        text: rt('actions.recall'),
        id: 'recall',
        disabled: !getIsActionAllowed(
          Third_Party_Response_Enum_Action.Recall,
          status
        ),
        onItemClick: () => {
          setAction(Third_Party_Response_Enum_Action.Recall);
          setIsModalVisible(true);
        },
      },
      {
        text: rt('actions.reject'),
        id: 'reject',
        disabled: !getIsActionAllowed(
          Third_Party_Response_Enum_Action.Reject,
          status
        ),
        onItemClick: () => {
          setAction(Third_Party_Response_Enum_Action.Reject);
          setIsModalVisible(true);
        },
      },
      {
        text: rt('actions.moreInformation'),
        id: 'moreInformation',
        disabled: !getIsActionAllowed(
          Third_Party_Response_Enum_Action.RequestMoreInformation,
          status
        ),
        onItemClick: () => {
          setAction(Third_Party_Response_Enum_Action.RequestMoreInformation);
          setIsModalVisible(true);
        },
      },
    ]);
  }, [data, rt]);

  const { Title } =
    data?.third_party_response_by_pk?.questionnaireTemplateVersion?.parent ||
    {};

  const rawSchema =
    data?.third_party_response_by_pk?.questionnaireTemplateVersion?.Schema;
  const rawUiSchema =
    data?.third_party_response_by_pk?.questionnaireTemplateVersion?.UISchema;
  const Schema = useMemo(
    () => parseQuestionnaireSchemaForRendering(rawSchema, defaultSchema),
    [rawSchema]
  );
  const UISchema = useMemo(
    () => parseQuestionnaireUiSchemaForRendering(rawUiSchema, defaultUISchema),
    [rawUiSchema]
  );
  const rawResponseData = data?.third_party_response_by_pk?.ResponseData;
  const responseFiles = data?.third_party_response_by_pk?.files;
  const jsonFormsData = useMemo(
    () => ({
      ...parseQuestionnaireResponseData(rawResponseData),
      files: responseFiles,
    }),
    [rawResponseData, responseFiles]
  );

  const {
    updateStatus: handleUpdateStatus,
    loading: handleUpdateStatusLoading,
  } = useUpdateThirdPartyResponseStatus();

  const onUpdateStatus = async (data: UpdateStatusSchemaFields) => {
    await handleUpdateStatus({
      Action: action,
      ResponseIds: [Id],
      Reason: data.Reason,
      RequestType: data.RequestType ? RequestTypeOptions[data.RequestType] : '',
      ShareWithRespondents: data.ShareWithRespondents,
      ThirdPartyId: thirdPartyId,
    });
  };

  const onApprove = useCallback(() => {
    setAction(Third_Party_Response_Enum_Action.Approve);
    setIsModalVisible(true);
  }, []);

  // Object-level toolbar (CUS-170) — flag-gated. This is the Phase 5
  // approve-cancel surface: the toolbar shows an Approve tick (wired to
  // `onApprove`) and a Cancel X (navigates back) instead of a Save disc, and
  // relocates the Recall / Reject / More information actions into the overflow ⋮
  // menu. Permissions resolve against the response's parent third party — the
  // same gate the legacy `Permission` wrapper applies.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title: Title ?? '' },
      parentObject: parent?.third_party ?? null,
      cancelMode: 'approve-cancel',
      onApprove,
      // Approve-tick parity with the legacy primary Approve button: same
      // permission gate (hidden when the user can't update the response's
      // parent third party) and same `disabled` guard (only enabled while the
      // response is awaiting review).
      approvePermission: 'update:questionnaire_template_version',
      approveCanHaveAccessAsContributor: true,
      approveDisabled: isDisabled,
      actions: {
        overflow: [
          {
            id: 'more-actions',
            label: rt('actions.moreActions'),
            actions: actions.map(
              (item): ToolbarActionConfig => ({
                id: item.id,
                label: item.text,
                onClick: item.onItemClick,
                disabled: item.disabled,
                permission: 'update:questionnaire_template_version',
                canHaveAccessAsContributor: true,
              })
            ),
          },
        ],
      },
    }),
    [Title, parent?.third_party, onApprove, actions, rt, isDisabled]
  );

  if (loading) {
    return (
      <PageLayout title={Title}>
        <Loading />
      </PageLayout>
    );
  }

  const selectedItems = data?.third_party_response_by_pk
    ? [{ Id, Status: data.third_party_response_by_pk.Status }]
    : [];

  return (
    <PageLayout
      title={Title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
    >
      <Container>
        <SpaceBetween size={'m'} direction={'vertical'}>
          <SpaceBetween size={'s'} direction={'horizontal'}>
            <Header variant={'h2'}>{'Status:'}</Header>
            <div className={'flex h-full items-center justify-center'}>
              <SimpleRatingBadge
                rating={getByValue(data?.third_party_response_by_pk?.Status)}
              />
            </div>
          </SpaceBetween>
          <JsonForms
            schema={Schema}
            uischema={UISchema}
            data={jsonFormsData}
            renderers={rendererRegistry}
            readonly={true}
          />
          {/* Legacy approve / more-actions / cancel controls — kept as the
              flag-off fallback. With `dynamic_forms` on, these relocate to the
              page-header ObjectToolbar (Approve tick + Cancel X + overflow ⋮). */}
          {!objectToolbarEnabled && (
            <SpaceBetween size={'s'} direction={'horizontal'}>
              <Permission
                permission={'update:questionnaire_template_version'}
                parentObject={parent?.third_party || null}
                canHaveAccessAsContributor={true}
              >
                <SpaceBetween size={'s'} direction={'horizontal'}>
                  <Button
                    disabled={isDisabled}
                    variant={'primary'}
                    onClick={onApprove}
                  >
                    {rt('actions.approve')}
                  </Button>
                  <ActionsButton
                    disabled={actions.every((action) => action.disabled)}
                    buttonText={rt('actions.moreActions')}
                    items={actions}
                  />
                </SpaceBetween>
              </Permission>
              <Button
                disabled={false}
                variant={'normal'}
                onClick={() => {
                  navigate(-1);
                }}
              >
                {'Cancel'}
              </Button>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>
      {/* CUS-243 — sits BELOW the response itself: the reviewer reads the
          submission first, then what was previously asked about it. Renders
          nothing at all when there is no history. PageLayout already spaces
          its children, so this is a sibling rather than a new wrapper. */}
      <ResponseRequestHistory responseId={Id} />
      <UpdateStatusModal
        onDismiss={() => setIsModalVisible(false)}
        onUpdateStatus={onUpdateStatus}
        action={action}
        loading={handleUpdateStatusLoading}
        isVisible={isModalVisible}
        selectedItems={selectedItems}
      />
    </PageLayout>
  );
};

export default Page;
